import { useState } from 'react';
import type { TraceRecord, TraceStep } from '../../crdt/agent';
import { recallRun, type StepRecord } from '../../state/agentTrace';
import StepInspector from './StepInspector';

/**
 * The steps an assistant run took, in two modes (docs/agent.md §4).
 *
 * Live, while a run is in flight, this is fed from the store. Afterwards the
 * same list is fed from the run's trace in the document — so the trace does not
 * vanish the moment the run finishes, which is when you actually want to look
 * at what it did.
 *
 * Both modes render the same rows and open the same inspector; the only
 * difference is how much detail the inspector can show, since raw responses are
 * kept for this page session only.
 */

/** The store's live step, narrowed to what a row needs. */
function fromLive(step: StepRecord): TraceStep {
  return {
    seq: step.seq,
    tool: step.tool,
    label: step.label,
    state: step.state === 'error' ? 'error' : 'ok',
    error: step.error,
    ms: step.ms,
    args: step.args,
    opArgs: step.opArgs,
    calls: step.calls,
  };
}

function callCount(step: TraceStep): number {
  return (step.calls ?? []).filter((c) => !c.cached).length;
}

function summarise(steps: TraceStep[]): string {
  const requests = steps.reduce((n, s) => n + callCount(s), 0);
  const ms = steps.reduce((n, s) => n + (s.ms ?? 0), 0);
  const bits = [`${steps.length} step${steps.length === 1 ? '' : 's'}`];
  if (requests) bits.push(`${requests} archive request${requests === 1 ? '' : 's'}`);
  if (ms >= 100) bits.push(`${(ms / 1000).toFixed(1)}s`);
  return bits.join(' · ');
}

function Row({
  step,
  detail,
  live,
}: {
  step: TraceStep;
  detail?: StepRecord | null;
  live?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const state = live && detail?.state === 'running' ? 'running' : step.state;
  return (
    <li className={`chat-activity-${state}`}>
      <span className="step-label">{step.label}</span>
      {step.state === 'error' && step.error ? (
        <span className="step-row-error"> — {step.error}</span>
      ) : null}
      {state !== 'running' && (
        <button
          className="step-inspect"
          onClick={() => setOpen(true)}
          title="Show the exact query and response"
        >
          inspect
        </button>
      )}
      {open && <StepInspector step={step} detail={detail} onClose={() => setOpen(false)} />}
    </li>
  );
}

/** The live trace, while a run this client started is in flight. */
export function LiveStepList({ steps }: { steps: StepRecord[] }) {
  if (!steps.length) return null;
  return (
    <ul className="chat-activity">
      {steps.map((step) => (
        <Row key={step.seq} step={fromLive(step)} detail={step} live />
      ))}
    </ul>
  );
}

/**
 * A finished run's trace, read back from the document. Collapsed by default —
 * the conversation is the thing you are reading; the steps are there when you
 * want to know how it got there.
 */
export function TraceRow({ trace }: { trace: TraceRecord }) {
  const [open, setOpen] = useState(false);
  if (!trace.steps.length) return null;
  // Present only for the person whose browser ran it, and only until reload.
  const detail = recallRun(trace.runId);

  return (
    <div className="chat-trace">
      <button className="step-toggle" aria-expanded={open} onClick={() => setOpen(!open)}>
        {open ? 'Hide' : 'Show'} what the assistant did ({summarise(trace.steps)})
      </button>
      {open && (
        <>
          <ul className="chat-activity">
            {trace.steps.map((step) => (
              <Row
                key={step.seq}
                step={step}
                detail={detail?.find((d) => d.seq === step.seq) ?? null}
              />
            ))}
          </ul>
          {trace.truncated ? (
            <p className="chat-trace-note">
              {trace.truncated} earlier step{trace.truncated === 1 ? '' : 's'} not kept.
            </p>
          ) : null}
          {trace.endReason !== 'complete' && (
            <p className="chat-trace-note">Run ended: {trace.endReason}.</p>
          )}
        </>
      )}
    </div>
  );
}
